import type { Event, Node } from "@melo/shared";
import type { Tables } from "../lib/store";
import { formatDate, keyOf, statusOf } from "../lib/select";
import { optionLabel } from "./pickers";

type Changes = Record<string, [unknown, unknown]>;

/** The item an event is about, if it still exists. */
export function eventNode(t: Tables, e: Event): Node | undefined {
  if (e.entity === "node") return t.nodes.get(e.entityId) ?? (e.data as Node | null) ?? undefined;
  if (e.entity === "claim") return t.nodes.get(e.entityId);
  const nodeId = (e.summary.nodeId as string | undefined) ?? ((e.data as { nodeId?: string } | null)?.nodeId ?? undefined);
  return nodeId ? t.nodes.get(nodeId) : undefined;
}

const ROLE_WORD: Record<string, string> = { producer: "a producer", member: "a member", contractor: "a contractor" };

/**
 * What a set of changes did, as a verb phrase about `target` ("moved FB-7 Dock props kit to Done",
 * "set Priority to High on 3 items"). An empty target means the item is implied.
 */
function describeChanges(t: Tables, n: Node | undefined, ch: Changes, target: string, fallback: string): string {
  const on = target ? ` on ${target}` : "";
  const obj = target ? ` ${target}` : " this";
  const statusLabel = (key: unknown) => (n ? (statusOf(t.kinds.get(n.kindId), String(key))?.label ?? String(key)) : String(key));
  if (ch.status) return `moved${target ? ` ${target}` : ""} to ${statusLabel(ch.status[1])}`;
  if (ch.title) return `renamed${target ? ` ${target}` : " this"} to "${String(ch.title[1])}"`;
  if (ch.parent) {
    const p = ch.parent[1] ? t.nodes.get(String(ch.parent[1])) : undefined;
    return p ? `moved${target ? ` ${target}` : ""} under ${p.title}` : `took${obj} out of its milestone`;
  }
  if (ch.assignees) {
    const ids = (ch.assignees[1] as string[] | null) ?? [];
    const first = ids[0] ? t.actors.get(ids[0]) : undefined;
    return first ? `assigned${obj} to ${first.name}` : `unassigned${obj}`;
  }
  if (ch.labels) {
    const [before, after] = ch.labels as [string[], string[]];
    const added = after.filter((l) => !before.includes(l)).map((l) => t.labels.get(l)?.name).filter(Boolean);
    const removed = before.filter((l) => !after.includes(l)).map((l) => t.labels.get(l)?.name).filter(Boolean);
    if (added.length && !removed.length) return `labeled${obj} ${added.join(", ")}`;
    if (removed.length && !added.length) return `removed the ${removed.join(", ")} label${removed.length > 1 ? "s" : ""}${target ? ` from ${target}` : ""}`;
    return `changed labels${on}`;
  }
  if (ch.dueDate) return ch.dueDate[1] ? `set the due date to ${formatDate(String(ch.dueDate[1]))}${on}` : `cleared the due date${on}`;
  if (ch.startDate) return `changed dates${on}`;
  if (ch.body) return `edited the notes${on}`;
  if (ch.cover) return `changed the cover${on}`;
  if (ch.kind) return `changed${obj} to a ${String(ch.kind[1])}`;
  const f = Object.keys(ch).find((k) => k.startsWith("fields."));
  if (f) {
    const def = (n ? t.kinds.get(n.kindId) : undefined)?.fields.find((d) => d.key === f.slice(7)) ?? { key: f.slice(7), label: f.slice(7) };
    const v = ch[f]![1];
    const text = Array.isArray(v) ? v.map((x) => optionLabel(def, String(x))).join(", ") : typeof v === "string" ? optionLabel(def, v) : String(v);
    return v === null ? `cleared ${def.label}${on}` : `set ${def.label} to ${text}${on}`;
  }
  return fallback;
}

/**
 * One plain sentence for an event. With `onItem`, the item itself is implied ("moved to Review");
 * otherwise it is named ("moved BW-12 to Review").
 */
export function describeEvent(t: Tables, e: Event, onItem = false): string {
  const n = eventNode(t, e);
  const name = n ? `${keyOf(t, n)} ${n.title}` : String(e.summary.title ?? "an item");
  const target = onItem ? "" : name;
  const it = onItem ? "" : ` ${name}`;
  const ch = (e.summary.changes ?? {}) as Changes;

  switch (e.command) {
    case "node.create":
      return e.summary.draft ? `proposed${onItem ? " this" : it}` : `created${onItem ? " this" : it}`;
    case "node.update":
    case "node.bulk":
      if (e.summary.archived !== undefined) return e.summary.archived ? `archived${onItem ? " this" : it}` : `restored${onItem ? " this" : it}`;
      return describeChanges(t, n, ch, target, `updated${onItem ? " this" : it}`);
    case "node.move":
      return describeChanges(t, n, ch, target, `reordered${onItem ? " this" : it}`);
    case "milestone.ship": {
      if (!e.summary.shipped) return describeChanges(t, n, ch, target, `moved${onItem ? " this" : it}`);
      const carried = Number(e.summary.carried ?? 0);
      const to = e.summary.moveTo ? t.nodes.get(String(e.summary.moveTo))?.title : null;
      return `shipped${onItem ? " this" : it}${carried ? `, moving ${carried} unfinished ${carried === 1 ? "item" : "items"} to ${to ?? "no milestone"}` : ""}`;
    }
    case "node.checklist": {
      const c = (e.summary.checklist ?? {}) as Record<string, string>;
      const on = onItem ? "" : ` on${it}`;
      if (c.checked) return `checked "${c.checked}"${on}`;
      if (c.unchecked) return `unchecked "${c.unchecked}"${on}`;
      if (c.added) return `added "${c.added}" to the checklist${on}`;
      if (c.removed) return `removed "${c.removed}" from the checklist${on}`;
      return `edited the checklist${on}`;
    }
    case "node.archive":
      return e.summary.archived ? `archived${onItem ? " this" : it}` : `restored${onItem ? " this" : it}`;
    case "node.assign":
      return `changed who works on${onItem ? " this" : it}`;
    case "node.block":
      return `${e.summary.blocked === "needs_input" ? "asked" : "blocked"}${onItem ? "" : it}: ${String(e.summary.reason ?? "")}`;
    case "node.unblock":
      return e.summary.answer ? `answered${onItem ? "" : ` on${it}`}: ${String(e.summary.answer)}` : `unblocked${onItem ? " this" : it}`;
    case "claim.take":
      return `started work on${onItem ? " this" : it}`;
    case "claim.heartbeat":
      return `checked in${onItem ? "" : ` on${it}`}${e.summary.note ? `: ${String(e.summary.note)}` : ""}`;
    case "claim.release":
      return e.actorId && e.via === "system" ? `released${onItem ? " this" : it} after the lease ran out` : `stopped work on${onItem ? " this" : it}`;
    case "claim.complete":
      return `finished${onItem ? " this" : it}${n && statusOf(t.kinds.get(n.kindId), n.status)?.category === "review" ? " and sent it to review" : ""}`;
    case "plan.decide":
      return e.summary.discarded ? `discarded${onItem ? " this" : it}` : e.summary.approved ? `approved${onItem ? " this" : it}` : "decided a plan";
    case "comment.add":
      return `commented on${onItem ? " this" : it}`;
    case "ref.add":
      return `added ${String(e.summary.title || "a reference")}${n && !onItem ? ` to ${n.title}` : ""}`;
    case "ref.update":
      return `edited reference ${String(e.summary.title || "")}`.trim();
    case "ref.archive":
      return `removed reference ${String(e.summary.title || "")}`.trim();
    case "edge.add":
      return `linked ${String(e.summary.from ?? "")} to ${String(e.summary.to ?? "")}`;
    case "edge.remove":
      return `removed a link from ${String(e.summary.from ?? "")}`;
    case "space.create":
      return `created the project ${String(e.summary.name ?? "")}`;
    case "space.update":
      return `changed the project settings`;
    case "kind.upsert":
      return `changed the item type ${String(e.summary.name ?? "")}`;
    case "view.upsert":
      return `changed the view ${String(e.summary.name ?? "")}`;
    case "view.remove":
      return `removed the view ${String(e.summary.name ?? "")}`;
    case "label.upsert":
      return `saved the label ${String(e.summary.name ?? "")}`;
    case "label.remove":
      return `deleted the label ${String(e.summary.name ?? "")}`;
    case "template.upsert":
      return `saved the template ${String(e.summary.name ?? "")}`;
    case "template.remove":
      return `deleted the template ${String(e.summary.name ?? "")}`;
    case "member.set":
      if (e.summary.removed) return `took ${String(e.summary.name ?? "someone")} off the project`;
      return e.summary.added ? `added ${String(e.summary.name ?? "someone")} as ${ROLE_WORD[String(e.summary.role)] ?? "a member"}` : `made ${String(e.summary.name ?? "someone")} ${ROLE_WORD[String(e.summary.role)] ?? "a member"}`;
    case "agent.create":
      return `added the agent ${String(e.summary.name ?? "")}`;
    case "actor.update":
      return `updated ${String(e.summary.name ?? "")}`;
    default:
      return "made a change";
  }
}

/** Events about the work and the people doing it. Setup, like saving a label or a template, stays out of feeds. */
export function isWorkEvent(e: Event): boolean {
  return e.entity === "node" || e.entity === "comment" || e.entity === "plan" || e.entity === "ref" || e.entity === "member" || e.entity === "claim";
}

export interface FeedEntry {
  key: string;
  events: Event[];
  first: Event;
}

/** Collapses one command's side effects into one line: a batch create is "created 8 items", a claim is one entry. */
export function feedEntries(events: Event[], limit = 50): FeedEntry[] {
  const out: FeedEntry[] = [];
  for (const e of events) {
    // Side effects the app did on its own, like a first picture becoming the cover.
    if (e.summary.auto) continue;
    // Work carried forward when an update ships is summed up on the ship line itself.
    if (e.summary.carried === true) continue;
    if (e.entity === "claim" && e.command !== "claim.heartbeat") continue;
    if (e.entity === "plan" && e.command === "node.create") continue;
    if (e.entity === "kind" && e.command === "space.create") continue;
    if (e.entity === "view" && e.command === "space.create") continue;
    if (e.entity === "template" && e.command === "space.create") continue;
    if (e.entity === "member" && e.command === "space.create") continue;
    if (e.entity === "comment" && e.command !== "comment.add") continue;
    if (e.entity === "node" && e.command === "node.archive" && e.summary.root === false) continue;
    const last = out.at(-1);
    // A batch create or a bulk edit is one line: same person, same command, moments apart.
    const sameBatch =
      last &&
      last.first.actorId === e.actorId &&
      last.first.command === e.command &&
      (e.command === "node.create" || (e.command === "node.bulk" && last.first.createdAt === e.createdAt) || (e.command === "node.checklist" && last.first.entityId === e.entityId)) &&
      Math.abs(new Date(last.first.createdAt).getTime() - new Date(e.createdAt).getTime()) < (e.command === "node.checklist" ? 10 * 60_000 : 3000);
    if (sameBatch) last.events.push(e);
    else out.push({ key: String(e.seq), events: [e], first: e });
    if (out.length > limit) break;
  }
  return out.slice(0, limit);
}

export function describeEntry(t: Tables, entry: FeedEntry): string {
  if (entry.events.length > 1 && entry.first.command === "node.create") {
    return `${entry.first.summary.draft ? "proposed" : "created"} ${entry.events.length} items`;
  }
  if (entry.events.length > 1 && entry.first.command === "node.checklist") {
    const kinds = entry.events.map((e) => Object.keys((e.summary.checklist ?? {}) as object)[0]);
    const n = entry.events.length;
    const node = eventNode(t, entry.first);
    const name = node ? `${keyOf(t, node)} ${node.title}` : String(entry.first.summary.title ?? "an item");
    if (kinds.every((k) => k === "added")) return `added ${n} checklist steps to ${name}`;
    if (kinds.every((k) => k === "checked")) return `checked ${n} steps on ${name}`;
    return `updated the checklist on ${name}`;
  }
  if (entry.events.length > 1 && entry.first.command === "node.bulk") {
    const e = entry.first;
    const target = `${entry.events.length} items`;
    if (e.summary.archived !== undefined) return `${e.summary.archived ? "archived" : "restored"} ${target}`;
    return describeChanges(t, eventNode(t, e), (e.summary.changes ?? {}) as Changes, target, `changed ${target}`);
  }
  return describeEvent(t, entry.first);
}
